import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ContentHeader from './ContentHeader'
import { useI18n, useRehabSelectedThemes } from 'contexts/AuthContext'
import { Checkbox, Button, Select } from 'antd'
import DropdownSelect from 'components/DropdownSelect/index'
import CareInput from 'components/CareInput/index'
import { useApplyTags, useCarePersonInfo } from 'hooks/careHooks'
import { ScreenKey } from './util'
import { getHealthieUserId, getLocalLanguage } from 'utils/authLocalStorage'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { formatPhoneNumber, isMobile, showNotification } from 'utils/basic'
import { emailMagicLinkPromise } from 'apis/FitOnCareAPI'
import _ from 'lodash'
import { logBrazeEvent } from 'utils/brazeInit'
import { useLoginSuccess } from 'hooks/useAutoLogin'
import { getTagId } from 'hooks/useApplyTags'
import LoadingDialog from 'components/LoadingDialog/index'
const { Option } = Select

export const applySkuTag = (sku, state, applyHealthieTags) => {
  const stateCode = state.code
  let tagId = ''

  let skuName = ''
  switch (sku) {
    case 'wlc':
      skuName = 'Digital-WLC'
      break
    case 'msk':
      skuName = 'Digital-MSK'
      break
    case 'pt':
      skuName = 'Digital-PT'
      break
    default:
      skuName = ''
      break
  }
  if (skuName) {
    tagId = getTagId(stateCode, skuName)
    if (tagId) {
      applyHealthieTags({ tagId })
    }
  }
}

const PersonalInfo = (props) => {
  const { i18n } = useI18n()
  const { supportForNewWelcome, promo, sku, email: fitonEmail } = props

  const { rehabSelectedThemes } = useRehabSelectedThemes()
  const { theme, themeId } = useMemo(() => {
    return {
      theme: rehabSelectedThemes?.map((theme) => theme.name).join(','),
      themeId: rehabSelectedThemes?.map((theme) => theme.id),
    }
  }, [rehabSelectedThemes])

  const [emailOnFocus, setEmailOnFocus] = useState()
  const [phoneOnFocus, setPhoneOnFocus] = useState()
  const [isLoadingHealthieUser, setIsLoadingHealthieUser] = useState(false)
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    state,
    setState,
    phone,
    setPhone,
    isPhoneValid,
    isEmailValid,
    email,
    setEmail,
    isPolicyChecked,
    setIsPolicyChecked,
    isTelehealthConsent,
    setIsTelehealthConsent,
    isNextButtonEnable,
    uploadPersonalInfo,
    stateOptions,
    getUserInfo,
    isLoading,
  } = useCarePersonInfo({
    utmCampaign: props.utmCampaign,
    utmSource: props.utmSource,
    utmContent: props.utmContent,
    sku,
    theme,
    themeId,
    onLoginSuccess: props.onLoginSuccess,
    supportForNewWelcome,
  })
  const { applyHealthieTags } = useApplyTags()
  const { openSkipScreen } = useLoginSuccess({
    ...props,
    sku,
    supportForNewWelcome,
    setCurrentKey: props.setCurrentKey,
    fitonEmail,
    email,
  })

  useEffect(() => {
    if (props.email) {
      setEmail(props.email)
    }
  }, [props.email])

  useEffect(() => {
    const userId = getHealthieUserId()
    if (userId) {
      setIsLoadingHealthieUser(true)
      getUserInfo({ id: userId, include_tags: true })
        .then((res) => {
          setIsLoadingHealthieUser(false)
        })
        .catch(() => {
          setIsLoadingHealthieUser(false)
        })
        .finally(() => {
          setIsLoadingHealthieUser(false)
        })
    }
    const params = {
      ...(props.utmSource && { utm_source: props.utmSource }),
      ...(props.utmCampaign && { utm_campaign: props.utmCampaign }),
      ...(props.utmContent && { utm_content: props.utmContent }),
      ...(!_.isEmpty(supportForNewWelcome) && {
        Variant: 'Prescreen',
        Qualified: supportForNewWelcome.isValid,
      }),
      ...(!_.isEmpty(state) && { State: state.name }),
      ...(!_.isEmpty(promo) && { 'Promo Code': promo }),
      ...(!_.isEmpty(sku) && { sku }),
      ...(!_.isEmpty(theme) && { theme }),
      language: getLocalLanguage(),
    }
    logAmplitudeEvent('Screen View: Onboarding - About', params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sendEmail = async () => {
    const resp = await emailMagicLinkPromise({
      email: email,
      stateId: state.id,
    })
    if (resp.code !== 200) {
      showNotification({
        type: 'error',
        title: 'Error',
        message: i18n(resp.msg),
      })
    } else {
      props.setCurrentKey(ScreenKey.VERIFY_EMAIL)
    }
  }

  const openNextScreen = useCallback(async () => {
    openSkipScreen(state, sku)
  }, [openSkipScreen, state, sku])

  return (
    <div className='info-sec'>
      <ContentHeader {...props} />
      <div className='info-content'>
        <div className='title'>{i18n('Tell us a bit about yourself')}</div>
        <div className='subtitle'>
          {i18n('Provide your contact information')}
        </div>
        <div className='name-sec'>
          <CareInput
            {...{
              inputValue: firstName,
              setInputValue: setFirstName,
              placeholder: i18n(`Legal First Name`),
            }}
          />
          <CareInput
            {...{
              inputValue: lastName,
              setInputValue: setLastName,
              placeholder: i18n(`Legal Last Name`),
            }}
          />
        </div>
        <DropdownSelect
          {...{
            placeholder: i18n('State'),
            options: stateOptions,
            setValue: (value) => {
              const currentOption = stateOptions.find(
                (option) => option.id === value
              )
              setState(currentOption)
            },
            value: state && state.id,
            className: `${isMobile() ? 'm-t-8' : 'm-t-18'}`,
            render: (
              <>
                {stateOptions.map((option, index) => (
                  <Option className='state-item' value={option.id} key={index}>
                    {option.name}
                  </Option>
                ))}
              </>
            ),
          }}
        />
        <CareInput
          {...{
            inputValue: formatPhoneNumber(
              (phone || '').replace('+1', '').replace(/\D/g, '')
            ),
            setInputValue: (value) => {
              if (value) {
                const formattedPhoneNumber = formatPhoneNumber(value)
                setPhone('+1 ' + formattedPhoneNumber)
              } else {
                setPhone('')
              }
            },
            placeholder: i18n(`000-000-0000`),
            prefix: '+1',
            isValid: !phone || phoneOnFocus || isPhoneValid,
            errorMessage: i18n('Invalid Phone'),
            className: `${isMobile() ? 'm-t-8' : 'm-t-18'}`,
            type: 'text',
            pattern: '\\d*',
            inputMode: 'numeric',
            onFocus: () => {
              setPhoneOnFocus(true)
            },
            onBlur: () => {
              setPhoneOnFocus(false)
            },
          }}
        />
        <CareInput
          {...{
            inputValue: email,
            setInputValue: (value) => {
              setEmail(value)
            },
            placeholder: i18n(`Email`),
            isValid: !email || emailOnFocus || isEmailValid,
            errorMessage: i18n('Invalid Email'),
            className: `${isMobile() ? 'm-t-8' : 'm-t-18'}`,
            type: 'email',
            onFocus: () => {
              setEmailOnFocus(true)
            },
            onBlur: () => {
              setEmailOnFocus(false)
            },
          }}
        />

        <div className='policy-tip'>
          {i18n(
            'By checking the boxes below, you agree to the following policies and agreements:'
          )}
        </div>
        <div className='policy-item'>
          <Checkbox
            checked={isPolicyChecked}
            onChange={(e) => setIsPolicyChecked(e.target.checked)}
          />
          <p className='terms'>
            {i18n(
              `I have read and agree to FitOn Care's {0} & {1}`,
              <a
                className='hover-link gradient-font-2 underline-link'
                target='_blank'
                rel='noopener noreferrer'
                href='https://fitoncare.com/privacy'
                key='privacy-policy'
              >
                {i18n(`Privacy Policy`)}
              </a>,
              <a
                className='hover-link gradient-font-2 underline-link'
                target='_blank'
                rel='noopener noreferrer'
                href='https://fitoncare.com/terms'
                key='terms-of-use'
              >
                {i18n(`Terms of Use`)}.
              </a>
            )}
          </p>
        </div>
        <div className='policy-item'>
          <Checkbox
            checked={isTelehealthConsent}
            onChange={(e) => setIsTelehealthConsent(e.target.checked)}
          />
          <p className='terms'>
            {i18n(
              `I have read and agree to FitOn Care's {0}`,
              <a
                className='hover-link gradient-font-2 underline-link'
                target='_blank'
                rel='noopener noreferrer'
                href={
                  state?.code === 'CA'
                    ? 'https://fitoncare.com/telehealth-consent-ca'
                    : 'https://fitoncare.com/telehealth-consent'
                }
                key='telehealth-consent'
              >
                {i18n(`Telehealth Consent`)}.
              </a>
            )}
          </p>
        </div>
        <Button
          className='btn-primary full-width-btn'
          style={isMobile() ? { width: '100%' } : {}}
          onClick={() => {
            logAmplitudeEvent('Click: Onboarding - About', {
              State: state.name,
              sku: sku,
              theme: theme,
              language: getLocalLanguage(),
            })
            uploadPersonalInfo({
              onSuccess: async () => {
                if (_.isEmpty(sku)) {
                  logBrazeEvent('Onboarding Start')
                }
                applySkuTag(sku, state, applyHealthieTags)
                openNextScreen()
              },
              onVerifyEmail: () => {
                sendEmail()
              },
            })
          }}
          disabled={!isNextButtonEnable || isLoadingHealthieUser}
          loading={isLoading || isLoadingHealthieUser}
        >
          {i18n('Next')}
        </Button>
      </div>
      <LoadingDialog
        visible={isLoadingHealthieUser}
        setVisible={setIsLoadingHealthieUser}
        center={true}
      />
    </div>
  )
}

export default PersonalInfo
